import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";

const SHEETS_API_BASE = "https://sheets.googleapis.com/v4/spreadsheets";
const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3";

export type ProductType = "sale" | "expense" | "both";

export interface Product {
  id: string;
  name: string;
  rate: number;
  costPrice?: number;
  category?: string;
  unit?: string;
  stock?: number;
  lowStockAlert?: number;
  isActive?: boolean;
  hsn?: string;
  gstRate?: number;
  taxType?: string;
  gstInclusive?: boolean;
  imageUrl?: string;
  barcode?: string;
  color?: string;
  isFavorite?: boolean;
  variants?: Record<string, { rate: number; stock?: number; costPrice?: number }>;
  scheme?: { buyQty: number; freeQty: number };
  productType?: ProductType;
  salePrice?: number; // alias of `rate` for Desktop compat
  syncStatus?: "synced" | "pending" | "failed";
}

export interface Transaction {
  id: string;
  orderId?: string;
  fullOrderId?: string;
  invoiceNumber?: string;
  productId: string;
  productName: string;
  quantity: number;
  rate: number;
  total: number;
  type: "sale" | "expense";
  createdAt: string;
  isGstBill?: boolean;
  hsn?: string;
  gstRate?: number;
  gstInclusive?: boolean;
  cgst?: number;
  sgst?: number;
  taxableAmount?: number;
  paymentMethod?: "cash" | "card" | "upi" | "split";
  variantId?: string;
  variantName?: string;
  // Soft-delete (GST bills only — preserves INV serial sequence for compliance)
  cancelled?: boolean;
  cancelledAt?: string;
}

const PRODUCT_COLORS = [
  "#BBDEFB",
  "#C8E6C9",
  "#FFF9C4",
  "#F8BBD9",
  "#FFE0B2",
  "#E1BEE7",
  "#B2DFDB",
  "#FFCCBC",
];

function getRandomColor(): string {
  return PRODUCT_COLORS[Math.floor(Math.random() * PRODUCT_COLORS.length)];
}

function getAccessToken(): string | null {
  return localStorage.getItem("googleAccessToken");
}

const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 30 * 1000;
const LOCAL_CACHE_PREFIX = "dh_sheets_cache_";

function getLocalCache(key: string): any | null {
  try {
    const raw = localStorage.getItem(
      LOCAL_CACHE_PREFIX + btoa(key).slice(0, 60),
    );
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.data;
    }
  } catch {}
  return null;
}

function setLocalCache(key: string, data: any) {
  try {
    const storageKey = LOCAL_CACHE_PREFIX + btoa(key).slice(0, 60);
    localStorage.setItem(
      storageKey,
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {}
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const isGet = options.method === "GET" || !options.method;
  const cacheKey = `${url}_${JSON.stringify(options.body || "")}`;

  if (isGet) {
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
  }

  const token = getAccessToken();
  if (!token) {
    if (isGet) {
      const localData = getLocalCache(cacheKey);
      if (localData) {
        window.dispatchEvent(new CustomEvent("google-token-expired"));
        return localData;
      }
    }
    throw new Error(
      "Your Google session has expired. Tap below to reconnect your account.",
    );
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 401) {
    if (isGet) {
      const localData = getLocalCache(cacheKey);
      if (localData) {
        window.dispatchEvent(new CustomEvent("google-token-expired"));
        return localData;
      }
    }
    window.dispatchEvent(new CustomEvent("google-token-expired"));
    throw new Error(
      "Your Google session has expired. Tap below to reconnect your account.",
    );
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message || "API request failed");
  }

  const data = await response.json();
  if (isGet) {
    cache.set(cacheKey, { data, timestamp: Date.now() });
    setLocalCache(cacheKey, data);
  } else {
    cache.clear();
  }
  return data;
}

// --- CORE SPREADSHEET FUNCTIONS ---

export async function listUserSpreadsheets() {
  const data = await fetchWithAuth(
    `${DRIVE_API_BASE}/files?q=mimeType='application/vnd.google-apps.spreadsheet' and (name contains 'DailyHisaab' or name contains 'Business Ledger' or name contains 'Daily Hisaab' or name contains 'dailyhisaab') and trashed = false&orderBy=modifiedTime desc&pageSize=50`,
  );
  return data.files || [];
}

export async function createSpreadsheet(title: string = "DailyHisaab Data") {
  const data = await fetchWithAuth(SHEETS_API_BASE, {
    method: "POST",
    body: JSON.stringify({
      properties: { title },
      sheets: [
        { properties: { title: "Products", index: 0 } },
        { properties: { title: "Transactions", index: 1 } },
      ],
    }),
  });
  await initializeSheetHeaders(data.spreadsheetId);
  return data;
}

async function initializeSheetHeaders(spreadsheetId: string) {
  const productsHeaders = [
    [
      "ID",
      "Name",
      "Sale Price",
      "Cost Price",
      "Product Type",
      "Category",
      "Color",
      "Barcode",
      "Is Favorite",
      "HSN",
      "GST Rate",
      "Tax Type",
      "GST Inclusive",
      "Stock",
    ],
  ];
  const transactionsHeaders = [
    [
      "ID",
      "Product ID",
      "Product Name",
      "Quantity",
      "Rate",
      "Total",
      "Type",
      "Created At",
    ],
  ];

  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!A1:N1?valueInputOption=RAW`,
    {
      method: "PUT",
      body: JSON.stringify({ values: productsHeaders }),
    },
  );

  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Transactions!A1:I1?valueInputOption=RAW`,
    {
      method: "PUT",
      body: JSON.stringify({ values: transactionsHeaders }),
    },
  );
}

// Ensure Stock header exists for existing users (one-time migration)
async function ensureStockHeader(spreadsheetId: string): Promise<void> {
  try {
    const headerData = await fetchWithAuth(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!N1`,
    );
    const currentHeader = headerData.values?.[0]?.[0] || "";
    if (currentHeader !== "Stock") {
      await fetchWithAuth(
        `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!N1?valueInputOption=RAW`,
        {
          method: "PUT",
          body: JSON.stringify({ values: [["Stock"]] }),
        },
      );
    }
  } catch (e) {
    console.warn("Could not ensure Stock header:", e);
  }
}

export async function getProducts(spreadsheetId: string): Promise<Product[]> {
  try {
    // Ensure Stock header exists for existing spreadsheets
    await ensureStockHeader(spreadsheetId);

    const data = await fetchWithAuth(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!A2:N1000`,
    );
    if (!data.values || data.values.length === 0) return [];

    return data.values
      .filter((row: string[]) => row[0])
      .map((row: string[]) => ({
        id: row[0] || "",
        name: row[1] || "",
        salePrice: parseFloat(row[2]) || 0,
        costPrice: parseFloat(row[3]) || 0,
        productType: (row[4] as ProductType) || "both",
        category: row[5] || "",
        color: row[6] || PRODUCT_COLORS[0],
        barcode: row[7] || "",
        isFavorite: row[8] === "Yes",
        hsn: row[9] || "",
        gstRate: parseFloat(row[10]) || 0,
        taxType: row[11] || "none",
        gstInclusive: row[12] === "TRUE",
        stock: parseInt(row[13]) || 0,
      }));
  } catch (error: any) {
    if (error.message?.includes("Unable to parse range")) return [];
    throw error;
  }
}

// --- SMART SEARCH HIERARCHY ---

export async function fetchProductDataZeroCost(
  barcode: string,
  spreadsheetId?: string,
): Promise<{
  name: string;
  brand?: string;
  img?: string;
  price?: number;
  source?: "external" | "firebase";
} | null> {
  // 1. External APIs
  const apis = [
    `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`,
    `https://world.openproductsfacts.org/api/v0/product/${barcode}.json`,
    `https://world.openbeautyfacts.org/api/v0/product/${barcode}.json`,
  ];

  for (const url of apis) {
    try {
      const resp = await fetch(url);
      const data = await resp.json();
      if (data.status === 1) {
        const info = {
          name:
            data.product.product_name ||
            data.product.generic_name ||
            "Unknown Product",
          brand: data.product.brands || "",
          img: data.product.image_front_small_url || "",
          price: 0,
          source: "external",
        };
        return info;
      }
    } catch (e) {
      continue;
    }
  }

  // 3. Firebase Master Catalog
  try {
    const docSnap = await getDoc(doc(db, "master_product_catalog", barcode));
    if (docSnap.exists()) {
      const info = docSnap.data() as any;
      return { ...info, source: "firebase" };
    }
  } catch (e) {
    console.error("Firebase lookup failed", e);
  }

  return null;
}

// --- SAVE & UPDATE LOGIC ---

export async function saveToMasterCatalog(
  barcode: string,
  productInfo: { name: string; brand?: string; img?: string },
) {
  try {
    const docRef = doc(db, "master_product_catalog", barcode);
    await setDoc(docRef, productInfo, { merge: true });
    localStorage.setItem(`product_${barcode}`, JSON.stringify(productInfo));
  } catch (e) {
    console.error("Global save failed", e);
  }
}

export async function addProduct(
  spreadsheetId: string,
  product: Omit<Product, "id">,
  isFromExternalSource: boolean = false,
): Promise<Product> {
  const id = `p_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const newProduct: Product = {
    ...product,
    id,
  };

  const row = [
    newProduct.id,
    newProduct.name,
    newProduct.rate.toString(),
    newProduct.costPrice?.toString() || "0",
    newProduct.productType || "both",
    newProduct.category || "",
    newProduct.color || PRODUCT_COLORS[0],
    newProduct.barcode || "",
    newProduct.isFavorite ? "Yes" : "No",
    newProduct.hsn || "",
    (newProduct.gstRate || 0).toString(),
    newProduct.taxType || "none",
    newProduct.gstInclusive ? "TRUE" : "FALSE",
    (newProduct.stock || 0).toString(),
  ];
  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!A:N:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`,
    {
      method: "POST",
      body: JSON.stringify({ values: [row] }),
    },
  );
  return newProduct;
}

export async function updateProduct(
  spreadsheetId: string,
  product: Product,
): Promise<Product> {
  const products = await getProducts(spreadsheetId);
  const index = products.findIndex((p) => p.id === product.id);
  if (index === -1) throw new Error("Product not found");

  const rowNumber = index + 2;
  const row = [
    product.id,
    product.name,
    ((product as any).salePrice ?? product.rate ?? 0).toString(),
    product.costPrice?.toString() || "0",
    product.productType || "both",
    product.category || "",
    product.color || PRODUCT_COLORS[0],
    product.barcode || "",
    product.isFavorite ? "Yes" : "No",
    product.hsn || "",
    (product.gstRate || 0).toString(),
    product.taxType || "none",
    product.gstInclusive ? "TRUE" : "FALSE",
    (product.stock || 0).toString(),
  ];

  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!A${rowNumber}:N${rowNumber}?valueInputOption=RAW`,
    {
      method: "PUT",
      body: JSON.stringify({ values: [row] }),
    },
  );
  return product;
}

// --- STOCK UPDATE FUNCTION ---

export async function updateProductStock(
  spreadsheetId: string,
  productId: string,
  quantityChange: number,
  operation: "add" | "deduct",
): Promise<void> {
  const products = await getProducts(spreadsheetId);
  const index = products.findIndex((p) => p.id === productId);
  if (index === -1) return;

  const product = products[index];
  const currentStock = product.stock || 0;
  const newStock =
    operation === "add"
      ? currentStock + quantityChange
      : Math.max(0, currentStock - quantityChange);

  const rowNumber = index + 2;
  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Products!N${rowNumber}?valueInputOption=RAW`,
    {
      method: "PUT",
      body: JSON.stringify({ values: [[newStock.toString()]] }),
    },
  );
}

// --- TRANSACTION & STATS FUNCTIONS ---

export async function deleteProduct(
  spreadsheetId: string,
  productId: string,
): Promise<void> {
  const products = await getProducts(spreadsheetId);
  const index = products.findIndex((p) => p.id === productId);
  if (index === -1) return;
  const rowNumber = index + 2;
  const spreadsheet = await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}`,
  );
  const sheetId = spreadsheet.sheets.find(
    (s: any) => s.properties.title === "Products",
  )?.properties.sheetId;
  if (sheetId !== undefined) {
    await fetchWithAuth(`${SHEETS_API_BASE}/${spreadsheetId}:batchUpdate`, {
      method: "POST",
      body: JSON.stringify({
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId,
                dimension: "ROWS",
                startIndex: rowNumber - 1,
                endIndex: rowNumber,
              },
            },
          },
        ],
      }),
    });
  }
}

export async function getTransactions(
  spreadsheetId: string,
): Promise<Transaction[]> {
  try {
    const data = await fetchWithAuth(
      `${SHEETS_API_BASE}/${spreadsheetId}/values/Transactions!A2:H10000`,
    );
    if (!data.values || data.values.length === 0) return [];
    return data.values
      .filter((row: string[]) => row[0])
      .map((row: string[]) => ({
        id: row[0] || "",
        productId: row[1] || "",
        productName: row[2] || "",
        quantity: parseInt(row[3]) || 0,
        rate: parseFloat(row[4]) || 0,
        total: parseFloat(row[5]) || 0,
        type: (row[6] as "sale" | "expense") || "sale",
        createdAt: row[7] || new Date().toISOString(),
      }));
  } catch (error: any) {
    console.error("getTransactions failed:", error?.message || error);
    if (error.message?.includes("Unable to parse range")) return [];
    throw error;
  }
}

export async function deleteTransaction(
  spreadsheetId: string,
  transactionId: string,
): Promise<void> {
  const transactions = await getTransactions(spreadsheetId);
  const index = transactions.findIndex((t) => t.id === transactionId);
  if (index === -1) return;
  const rowNumber = index + 2;
  const spreadsheet = await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}`,
  );
  const sheetId = spreadsheet.sheets.find(
    (s: any) => s.properties.title === "Transactions",
  )?.properties.sheetId;
  if (sheetId !== undefined) {
    await fetchWithAuth(`${SHEETS_API_BASE}/${spreadsheetId}:batchUpdate`, {
      method: "POST",
      body: JSON.stringify({
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId,
                dimension: "ROWS",
                startIndex: rowNumber - 1,
                endIndex: rowNumber,
              },
            },
          },
        ],
      }),
    });
  }
}

export async function addTransactions(
  spreadsheetId: string,
  items: {
    productId: string;
    productName: string;
    quantity: number;
    rate: number;
  }[],
  type: "sale" | "expense",
  customOrderId?: string,
): Promise<Transaction[]> {
  // FIXED: Use ISO format for reliable storage and parsing
  const now = new Date();
  const isoDate = now.toISOString();

  const orderId =
    customOrderId ||
    `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const transactions: Transaction[] = items.map((item, i) => ({
    id: `${orderId}_${i}`,
    productId: item.productId,
    productName: item.productName,
    quantity: item.quantity,
    rate: item.rate,
    total:
      type === "expense"
        ? -(item.quantity * item.rate)
        : item.quantity * item.rate,
    type,
    createdAt: isoDate, // FIXED: Store ISO format in Google Sheets
  }));
  const rows = transactions.map((t) => [
    t.id,
    t.productId,
    t.productName,
    t.quantity.toString(),
    t.rate.toString(),
    t.total.toString(),
    t.type,
    t.createdAt,
  ]);
  await fetchWithAuth(
    `${SHEETS_API_BASE}/${spreadsheetId}/values/Transactions!A:H:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`,
    {
      method: "POST",
      body: JSON.stringify({ values: rows }),
    },
  );
  return transactions;
}

export function calculateDashboardStats(transactions: Transaction[]) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const stats = {
    todaySales: 0,
    todayExpenses: 0,
    weekSales: 0,
    weekExpenses: 0,
    monthSales: 0,
    monthExpenses: 0,
    yearSales: 0,
    yearExpenses: 0,
  };

  transactions.forEach((t) => {
    const date = new Date(t.createdAt);
    const isSale = t.type === "sale";
    if (date >= startOfDay) {
      if (isSale) stats.todaySales += t.total;
      else stats.todayExpenses += t.total;
    }
    if (date >= startOfWeek) {
      if (isSale) stats.weekSales += t.total;
      else stats.weekExpenses += t.total;
    }
    if (date >= startOfMonth) {
      if (isSale) stats.monthSales += t.total;
      else stats.monthExpenses += t.total;
    }
    if (date >= startOfYear) {
      if (isSale) stats.yearSales += t.total;
      else stats.yearExpenses += t.total;
    }
  });
  return stats;
}

export async function saveCustomerPhone(
  spreadsheetId: string,
  phone: string,
): Promise<void> {
  const cleanPhone = phone.replace(/\D/g, "");
  if (cleanPhone.length < 10) return;

  try {
    const response = await fetch("/api/save-customer-phone", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        spreadsheetId,
        phoneNumber: cleanPhone,
      }),
    });

    if (!response.ok) {
      console.error("Failed to save customer phone to backend");
    }
  } catch (error) {
    console.error("Error saving customer phone:", error);
    // Silently fail - don't block WhatsApp sharing
  }
}

export function calculateWeeklyChartData(transactions: Transaction[]) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const now = new Date();
  const result = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    const dayStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const dayTransactions = transactions.filter((t) => {
      const tDate = new Date(t.createdAt);
      return tDate >= dayStart && tDate < dayEnd;
    });
    const sales = dayTransactions
      .filter((t) => t.type === "sale")
      .reduce((sum, t) => sum + t.total, 0);
    const expenses = dayTransactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + t.total, 0);
    result.push({ day: days[date.getDay()], sales, expenses });
  }
  return result;
}
